import React, { useRef, useImperativeHandle, forwardRef } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { Field, FieldRef } from "../Field/Field";
import { SectionProps, StaffState } from "types/staff-edit.types";
import { ImagePayload } from "types/image.type"; 
import {
  checkAccountNumber,
  CustomerRules,
  onlyStringRule,
} from "utils/formRules";
import UploadImageButton from "components/common/UploadImageButton/UploadButton";

// Định nghĩa interface cho Ref của Section này
export interface BasicInfoSectionRef {
  validateAll: () => Promise<boolean>;
}

export const BasicInfoSection = forwardRef<
  BasicInfoSectionRef,
  SectionProps<StaffState["basicInfo"]>
>(({ data, onUpdate }, ref) => {

  // Tạo các Ref để quản lý từng Field
  const birthDateRef = useRef<FieldRef>(null);
  const nationalityRef = useRef<FieldRef>(null);
  const genderRef = useRef<FieldRef>(null);
  const ethnicityRef = useRef<FieldRef>(null);
  const maritalStatusRef = useRef<FieldRef>(null);
  const bankAccountRef = useRef<FieldRef>(null);
  const bankNameRef = useRef<FieldRef>(null);

  // Công khai hàm validateAll cho StaffAddPage gọi
  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const results = await Promise.all([
        birthDateRef.current?.validate(),
        nationalityRef.current?.validate(),
        genderRef.current?.validate(),
        ethnicityRef.current?.validate(),
        maritalStatusRef.current?.validate(),
        bankAccountRef.current?.validate(),
        bankNameRef.current?.validate(),
      ]);
      // Trả về true nếu tất cả kết quả đều là true (không có lỗi)
      return results.every((r) => r === true);
    },
  }));

  return (
    <FourColumnRowSection
      leftTitle="Thông tin cơ bản"
      rows={[
        {
          leftLabel: "Ngày sinh",
          leftValue: (
            <Field
              ref={birthDateRef}
              type="date"
              path="basicInfo.birthDate"
              value={data.birthDate}
              onUpdate={onUpdate}
              placeholder="Chọn ngày sinh"
              rules={CustomerRules}
            />
          ),
          rightLabel: "Quốc tịch",
          rightValue: (
            <Field
              ref={nationalityRef}
              path="basicInfo.nation"
              value={data.nation}
              options={["Việt Nam", "Thái Lan", "Ấn Độ"]}
              onUpdate={onUpdate}
              rules={CustomerRules}
              placeholder="Nhập quốc tịch"
            />
          ),
        },
        {
          leftLabel: "Giới tính",
          leftValue: (
            <Field
              ref={genderRef}
              path="basicInfo.gender"
              value={data.gender}
              onUpdate={onUpdate}
              rules={CustomerRules}
              options={["Nam", "Nữ", "Khác"]}
              placeholder="Chọn giới tính"
            />
          ),
          rightLabel: "Dân tộc",
          rightValue: (
            <Field
              ref={ethnicityRef}
              path="basicInfo.ethnicity"
              value={data.ethnicity}
              options={["Kinh", "Tày", "Thái", "Hoa", "Khmer"]}
              onUpdate={onUpdate}
              rules={CustomerRules}
              placeholder="Nhập dân tộc"
            />
          ),
        },
        {
          leftLabel: "Tình trạng hôn nhân",
          leftValue: (
            <Field
              ref={maritalStatusRef}
              path="basicInfo.maritalStatus"
              value={data.maritalStatus}
              onUpdate={onUpdate}
              rules={CustomerRules}
              placeholder="Nhập tình trạng hôn nhân"
            />
          ),
          rightLabel: "Tài khoản ngân hàng",
          rightValue: (
            <div className="flex items-center mt-0 gap-2">
              <Field
                ref={bankAccountRef}
                path="basicInfo.bankAccount.accountNumber"
                value={data.bankAccount.accountNumber}
                onUpdate={onUpdate}
                rules={checkAccountNumber()}
                placeholder="Nhập số tài khoản"
              />
              <Field
                ref={bankNameRef}
                path="basicInfo.bankAccount.bankName"
                value={data.bankAccount.bankName}
                onUpdate={onUpdate}
                rules={[...CustomerRules, onlyStringRule()]}
                options={[
                  "Vietcombank",
                  "Techcombank",
                  "MB Bank",
                  "TP Bank",
                  "ACB",
                  "VietinBank",
                ]}
                placeholder="Chọn ngân hàng"
              />
              
              <UploadImageButton
                label="Tải lên QR"
                isDisabled={false}
                imagesData={data.bankAccount.bankLogo || []}
                setImagesData={(action) => {
                  const currentImages: ImagePayload[] = data.bankAccount.bankLogo || [];
                  
                  const updatedImages: ImagePayload[] = typeof action === 'function' 
                    ? action(currentImages) 
                    : action;

                  onUpdate("basicInfo.bankAccount.bankLogo", updatedImages);
                }}
              />
            </div>
          ),
        },
      ]}
      rightTitle=""
    />
  );
});

BasicInfoSection.displayName = "BasicInfoSection";